import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HospitalTitlecasePipe } from "../../../../../../pipes/hospital-titlecase.pipe";

export type SegmentType = 'start' | 'openToAccess' | 'aggiudicazioneGara' | 'openToMarket';

export interface DashboardData {
  stepZero: {
    dueDate: string;
    endDate: string | null;
    status: 'COMPLETED' | 'OPEN' | 'IN_PROGRESS' | '';
  };
  openToAccess: {
    regionId: number;
    regionName: string;
    dueDate: string | null;
    endDate: string | null;
    status: 'COMPLETED' | 'OPEN' | 'IN_PROGRESS' | '';
  }[];
  aggiudicazioneGara: {
    regionId: number;
    regionName: string;
    dueDate: string | null;
    endDate: string | null;
    status: 'COMPLETED' | 'OPEN' | 'IN_PROGRESS' | '';
  }[];
  openToMarket: {
    regionId: number;
    regionName: string;
    hospitals: {
      onekey: string;
      name: string | null;
      city: string | null;
      dueDate: string | null;
      endDate: string | null;
      status: 'COMPLETED' | 'OPEN' | 'IN_PROGRESS' | '';
    }[];
  }[];
  metrics: {
    stepZeroCount: number;
    openToAccessCount: number;
    aggiudicazioneGaraCount: number;
    openToMarketHospitalCount: number;
  };
}

type TableRow = {
  regionName: string;
  dueDate: string | null;
  endDate: string | null;
  status: 'COMPLETED' | 'OPEN' | 'IN_PROGRESS' | '';
};

@Component({
  selector: 'app-modal-inclusion-process-tool-national',
  standalone: true,
  imports: [CommonModule, HospitalTitlecasePipe],
  templateUrl: './modal-inclusion-process-tool-national.component.html',
  styleUrl: './modal-inclusion-process-tool-national.component.css'
})
export class ModalInclusionProcessToolNationalComponent {
  @Input() segment!: SegmentType;
  @Input() data!: DashboardData;
  @Output() closeModal = new EventEmitter<void>();

  expandedRegions = new Set<number>();

  onCloseModal(): void {
    this.closeModal.emit();
  }

  formatDate(value: string | null | undefined): string {
    if (!value || value.trim() === '') return '-';
    let date: Date;
    if (value.includes('-')) {
      date = new Date(value);
    } else if (value.length === 8) {
      const year  = parseInt(value.substring(0, 4), 10);
      const month = parseInt(value.substring(4, 6), 10) - 1;
      const day   = parseInt(value.substring(6, 8), 10);
      date = new Date(year, month, day);
    } else {
      return '-';
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  }

  get modalTitle(): string {
    const titles: Record<SegmentType, string> = {
      start:              'Step Start',
      openToAccess:       'Inclusione Regionale',
      aggiudicazioneGara: 'Aggiudicazione Gara',
      openToMarket:       'Possibilità di Acquisto',
    };
    return titles[this.segment] ?? '';
  }

get isLayoutStart(): boolean {
  return this.segment === 'start';
}

get isLayoutA(): boolean {
  return this.segment === 'openToAccess' || this.segment === 'aggiudicazioneGara';
}

get tableRows(): TableRow[] {
  if (this.segment === 'openToAccess') return this.data.openToAccess;
  if (this.segment === 'aggiudicazioneGara') return this.data.aggiudicazioneGara;
  return [];
}

  toggleRegion(regionId: number): void {
    if (this.expandedRegions.has(regionId)) {
      this.expandedRegions.delete(regionId);
    } else {
      this.expandedRegions.add(regionId);
    }
  }

  isRegionExpanded(regionId: number): boolean {
    return this.expandedRegions.has(regionId);
  }

  getStatusClass(status: string, dateRow?: string | null): string {
    if (status !== 'COMPLETED' && dateRow && dateRow.trim() !== '') {
      const currentDate = new Date();
      let date: Date;
      if (dateRow.includes('-')) {
        date = new Date(dateRow);
      } else if (dateRow.length === 8) {
        const year  = parseInt(dateRow.substring(0, 4), 10);
        const month = parseInt(dateRow.substring(4, 6), 10) - 1;
        const day   = parseInt(dateRow.substring(6, 8), 10);
        date = new Date(year, month, day);
      } else {
        date = currentDate;
      }
      if (!isNaN(date.getTime()) && date < currentDate) {
        return 'bg-red-100 text-red-800';
      }
    }
    switch (status) {
      case 'COMPLETED':   return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED':     return 'bg-red-100 text-red-800';
      case 'OPEN':        return 'bg-gray-100 text-gray-800';
      default:            return 'bg-gray-200 text-gray-600';
    }
  }

  getStatusLabel(status: string, dateRow?: string | null): string {
    if (status !== 'COMPLETED' && dateRow && dateRow.trim() !== '') {
      const currentDate = new Date();
      let date: Date;
      if (dateRow.includes('-')) {
        date = new Date(dateRow);
      } else if (dateRow.length === 8) {
        const year  = parseInt(dateRow.substring(0, 4), 10);
        const month = parseInt(dateRow.substring(4, 6), 10) - 1;
        const day   = parseInt(dateRow.substring(6, 8), 10);
        date = new Date(year, month, day);
      } else {
        date = currentDate;
      }
      if (!isNaN(date.getTime()) && date < currentDate) {
        return 'In Ritardo';
      }
    }
    const statusMap: { [key: string]: string } = {
      COMPLETED:   'Completato',
      IN_PROGRESS: 'In Corso',
      EXPIRED:     'In Ritardo',
      OPEN:        'Non Completato',
    };
    return statusMap[status] ?? '-';
  }

  get sortedOpenToMarket() {
  return [...this.data.openToMarket].sort((a, b) =>
    a.regionName.localeCompare(b.regionName, 'it', { sensitivity: 'base' })
  );
}
}